/* Блоки быстрой записи: услуги, время, источник и комментарий. */
import {
  CLASS_MULT,
  MIN,
  dayLabel,
  duration,
  money,
  priceFor,
  sum,
  time,
  type Bay,
  type BusyRange,
  type OrderItem,
  type Service,
  type SlotOption,
  type Vertical,
} from "@okno/shared";
import { Icon } from "../../ui/Icon";

export type BookingSource = "phone" | "walkin" | "online";

export interface SlotPos {
  bayId: string;
  start: number;
}

interface ServicesProps {
  services: Service[];
  bay: Bay | null;
  vertical: Vertical;
  cls: number;
  selected: string[];
  items: OrderItem[];
  onToggle: (id: string) => void;
}

export function ServicesField({ services, bay, vertical, cls, selected, items, onToggle }: ServicesProps) {
  const list = services.filter((s) => s.active && (!bay || bay.cats.includes(s.category)));
  return (
    <div className="field">
      <span>Услуги{bay ? " · " + bay.name : ""}</span>
      <div className="svc-grid">
        {list.map((s) => {
          // цена на кнопке — подсказка по классу авто; итог считает сервер
          const p = priceFor(s, cls, CLASS_MULT[vertical]);
          return (
            <button key={s.id} className="svc" aria-pressed={selected.includes(s.id)} onClick={() => onToggle(s.id)}>
              <span className="svc-name">{s.name}</span>
              <span className="svc-meta">
                {money(p.price)} · {duration(p.dur)}
              </span>
            </button>
          );
        })}
      </div>
      {items.length ? (
        <span className="hint">
          Итого {money(sum(items, (x) => x.price))} · {duration(sum(items, (x) => x.dur))}
        </span>
      ) : null}
    </div>
  );
}

interface FixedSlotProps {
  slot: SlotPos;
  bay: Bay | undefined;
  dur: number;
  cf: BusyRange | null;
  onUnfix: () => void;
}

export function FixedSlotField({ slot, bay, dur, cf, onUnfix }: FixedSlotProps) {
  return (
    <div className="field">
      <span>Время</span>
      <div className={"callout " + (cf ? "callout-bad" : "")}>
        <Icon name={cf ? "alert" : "clock"} size={16} />
        <span>
          {dayLabel(slot.start)}, {time(slot.start)}–{time(slot.start + dur * MIN)} · {bay?.name || ""}
          {cf ? (
            <>
              <br />
              Не помещается: пост занят с {time(cf.start)}. Уберите услугу или выберите другое время.
            </>
          ) : null}
        </span>
      </div>
      <button className="btn btn-sm btn-ghost" style={{ alignSelf: "flex-start" }} onClick={onUnfix}>
        Подобрать другое время
      </button>
    </div>
  );
}

interface SlotsProps {
  slots: SlotOption[];
  bays: Bay[];
  selected: SlotPos | null;
  isLoading: boolean;
  isError: boolean;
  onPick: (s: SlotPos) => void;
}

export function SlotsField({ slots, bays, selected, isLoading, isError, onPick }: SlotsProps) {
  if (!slots.length) {
    const text = isLoading ? "Подбираем время…" : isError ? "Не удалось подобрать время — попробуйте ещё раз." : "Свободного времени в ближайшие три дня нет.";
    return (
      <div className="field">
        <span>Ближайшее свободное время</span>
        <p className="muted" style={{ fontSize: 14 }}>
          {text}
        </p>
      </div>
    );
  }
  return (
    <div className="field">
      <span>Ближайшее свободное время</span>
      <div className="slot-grid">
        {slots.map((s) => {
          const on = !!selected && selected.start === s.start && selected.bayId === s.bayId;
          const day = dayLabel(s.start);
          return (
            <button key={s.bayId + s.start} className="slot" aria-pressed={on} onClick={() => onPick({ bayId: s.bayId, start: s.start })}>
              <strong>{time(s.start)}</strong>
              <span>
                {day === "Сегодня" ? "" : day + " · "}
                {bays.find((b) => b.id === s.bayId)?.name || ""}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

export function EmptySlotField() {
  return (
    <div className="field">
      <span>Время</span>
      <p className="muted" style={{ fontSize: 14 }}>
        Выберите услуги — подберём ближайшее свободное время.
      </p>
    </div>
  );
}

interface ExtraProps {
  source: BookingSource;
  comment: string;
  onSource: (s: BookingSource) => void;
  onComment: (s: string) => void;
}

const SOURCES: [BookingSource, string][] = [
  ["phone", "Звонок"],
  ["walkin", "Живая очередь"],
  ["online", "Онлайн-запись"],
];

export function ExtraFields({ source, comment, onSource, onComment }: ExtraProps) {
  return (
    <div className="form-grid">
      <label className="field">
        <span>Источник</span>
        <select className="select" value={source} onChange={(e) => onSource(e.target.value as BookingSource)}>
          {SOURCES.map(([k, v]) => (
            <option key={k} value={k}>
              {v}
            </option>
          ))}
        </select>
      </label>
      <label className="field">
        <span>Комментарий</span>
        <input className="input" value={comment} placeholder="Необязательно" maxLength={2000} onChange={(e) => onComment(e.target.value)} />
      </label>
    </div>
  );
}
